import {
	ReactElement,
	PropsWithChildren,
	useState,
	useRef,
	useCallback,
	RefObject
} from "react";
import classNames from "classnames";
import {
	AppIcon,
	DateString,
	DropOverActionList,
	Icon,
	PersonAvatar,
	ButtonBase,
	IconButton,
	ButtonClickEventArgs,
	IconValue
} from "client/Components";
import { Coordinate } from "client/Misc/commonTypes";
import { getReactColorAttributes } from "client_legacy/ndaw/appColor";
import { AppColorViewModel } from "client/Api/Apps/common";
import { UnreachableCaseError } from "client_legacy/ndaw/errors";
import { AppImageDisplayInfo } from "client/Api/AppIcon/common";
import { generateTrackingId, setTrackingId } from "client/Misc/utils";
import styles from "./ActivityCard.module.less";
import { TimeZone } from "client/DateAndTime/timezones";

export enum ActivityCardIconKind {
	None = 0,
	AppIcon = 1,
	Icon = 2,
	PersonAvatar = 4
};

export type PersonAvatarData = {
	initials: string;
	imageSource: string | null;
};

export type ActivityCardIconData = {
	isTransparent: boolean;
} & (
		{
			kind: ActivityCardIconKind.None;
		} | {
			kind: ActivityCardIconKind.AppIcon;
			appIcon: AppImageDisplayInfo;
		} | {
			kind: ActivityCardIconKind.Icon;
			icon: IconValue;
		} | {
			kind: ActivityCardIconKind.PersonAvatar;
			personAvatar: PersonAvatarData;
		}
	);

export type ActivityCardProps = {
	isDismissed: boolean;
	color: AppColorViewModel;
	icon: ActivityCardIconData;
	categoryName: string;
	onDismiss?(): void;
	hasFooter?: boolean;
	isNotInteractable?: boolean;
};

type ActionsMenuState = {
	isVisible: false;
} | {
	isVisible: true;
	coordinate: Coordinate;
};

export const ActivityCard = (props: PropsWithChildren<ActivityCardProps>): ReactElement => {
	const {
		isDismissed,
		color,
		icon,
		categoryName,
		onDismiss,
		hasFooter = false,
		isNotInteractable = false,
		children
	} = props;

	const { colorClass, colorStyle } = getReactColorAttributes(color);

	const renderIconContent = (): ReactElement | null => {
		switch (icon.kind) {
			case ActivityCardIconKind.None:
				return null;
			case ActivityCardIconKind.AppIcon:
				return (
					<div className={styles.iconImage}>
						<AppIcon value={icon.appIcon} color="inherit" />
					</div>
				);
			case ActivityCardIconKind.Icon:
				return (
					<Icon
						value={icon.icon}
						size="default"
					/>
				);
			case ActivityCardIconKind.PersonAvatar:
				const { initials, imageSource } = icon.personAvatar;
				return (
					<PersonAvatar
						initials={initials}
						imageSrc={imageSource}
						size="x-small"
					/>
				);
			default:
				throw new UnreachableCaseError(icon);
		}
	};

	const dismissHandler = (): void => {
		if (isDismissed || !onDismiss) {
			return;
		}

		onDismiss();
	};

	return (
		<div
			{...setTrackingId("activity-card")}
			className={classNames(
				styles.root,
				{ [styles.root_noInteraction]: isNotInteractable },
				colorClass
			)}
			onMouseEnter={dismissHandler}
			onClick={dismissHandler}
			style={colorStyle}
		>

			<div className={styles.header}>
				<div className={classNames(
					styles.icon,
					{ [styles.icon_transparent]: icon.isTransparent }
				)}>
					{renderIconContent()}
				</div>
				<span
					{...setTrackingId("category")}
					className={styles.category}
				>
					{categoryName}
				</span>
				{!isDismissed && (
					<div
						{...setTrackingId("new-indicator")}
						className={styles.newIndicator}
					/>
				)}
			</div>

			<div className={classNames(
				styles.details,
				{ [styles.details_withFooter]: hasFooter }
			)}>
				{children}
			</div>
		</div>
	);
};

ActivityCard.displayName = "ActivityCard";

const Description = (props: PropsWithChildren<{}>): ReactElement => {
	const {
		children
	} = props;

	return (
		<div className={styles.description}>
			{children}
		</div>
	);
};

Description.displayName = "ActivityCard.Description";

ActivityCard.Description = Description;

const Footer = (props: PropsWithChildren<{}>): ReactElement => {
	const {
		children
	} = props;

	return (
		<span className={styles.footer}>
			{children}
		</span>
	);
};

Footer.displayName = "ActivityCard.Footer";

ActivityCard.Footer = Footer;

export type ActivityCardTimeIndicationProps = {
	timestamp: Date;
	timezone: TimeZone;
};

const TimeIndication = (props: ActivityCardTimeIndicationProps): ReactElement => {
	const {
		timestamp,
		timezone
	} = props;

	return (
		<span
			{...setTrackingId("time-indication")}
			className={styles.timeIndication}
		>
			<DateString
				value={timestamp}
				format="RelativeTimestamp"
				timezone={timezone}
			/>
		</span>
	);
};

Footer.TimeIndication = TimeIndication;

TimeIndication.displayName = "ActivityCard.Footer.TimeIndication";

export type ActivityCardFooterButtonProps = {
	onClick(): void;
	bubbleClick?: boolean;
};

const FooterButton = (props: PropsWithChildren<ActivityCardFooterButtonProps>): ReactElement => {
	const {
		onClick,
		bubbleClick,
		children
	} = props;

	const clickHandler = useCallback(
		(e: React.MouseEvent) => {
			if (bubbleClick === false) {
				e.preventDefault();
				e.stopPropagation();
			}

			if (onClick) {
				onClick();
			}
		},
		[bubbleClick, onClick]
	);

	return (
		<ButtonBase
			trackingId="activity-card-footer-button"
			onClick={clickHandler}
			className={styles.footerButton}
		>
			{children}
		</ButtonBase>
	);
};

FooterButton.displayName = "ActivityCard.Footer.Button";
Footer.Button = FooterButton;

export type ActivityFeedActionListProps = {
	trackingId: string;
	menuTitle: string;
};

const ActivityFeedActionList = (props: PropsWithChildren<ActivityFeedActionListProps>): ReactElement => {
	const {
		trackingId,
		menuTitle,
		children
	} = props;

	const [actionsMenu, setActionsMenu] = useState<ActionsMenuState>({ isVisible: false });
	const actionsMenuButton: RefObject<HTMLButtonElement | null> = useRef<HTMLButtonElement>(null);

	const actionsButtonClickHandler = (args: ButtonClickEventArgs): void => {
		const {
			clientX,
			clientY
		} = args;

		setActionsMenu({
			isVisible: true,
			coordinate: { x: clientX, y: clientY }
		});
	};

	return (
		<>
			<div
				{...setTrackingId(trackingId)}
				className={classNames(
					styles.actions,
					{ [styles.actions_visible]: actionsMenu.isVisible }
				)}
			>
				<IconButton
					icon="ellipsis"
					kind="transparent"
					onClick={actionsButtonClickHandler}
					ref={actionsMenuButton}
					bubbleClick={false}
					trackingId="more-button"
					shape="round"
					isActive={actionsMenu.isVisible}
				/>
			</div>
			{actionsMenu.isVisible && actionsMenuButton.current && (
				<DropOverActionList
					trackingId={generateTrackingId(trackingId, "__dropover")}
					bubbleClick={false}
					target={actionsMenuButton.current}
					targetAlignment={{ kind: "absolute", coordinate: actionsMenu.coordinate }}
					contentAlignment="tr"
					onClose={() => setActionsMenu({ isVisible: false })}
					menuTitle={menuTitle}
				>
					{children}
				</DropOverActionList>
			)}
		</>
	);
};

ActivityFeedActionList.displayName = "ActivityFeed.Footer.ActionList";

Footer.ActionList = ActivityFeedActionList;